<template>
  <div>
    <new-site-dialog
      @close="isNewSiteDialogOpened = false"
      @created="newSiteCreated"
      :client-id="clientId"
      :maker-id="makerId"
      v-if="isNewSiteDialogOpened"
    />
    <confirm-dialog
      v-model="isConfirmRemoveDialogOpened"
      @no="isConfirmRemoveDialogOpened = false"
      @yes="remove"
    />
    <v-form @submit.prevent.stop="$canWrite && (submit())">
      <div class="pa-5">
        <v-alert
          :value="true"
          type="error"
          class="mb-5"
          v-if="errors.length"
        >
          Veuillez corriger les erreurs suivantes :
          <ul>
            <li v-for="(error, i) in errors" :key="`error${i}`">
              {{ error }}
            </li>
          </ul>
        </v-alert>
        <v-row justify="center" align="center">
          <v-col cols="4">
            <v-text-field v-model="number" label="Numéro" outlined hide-details :autofocus="canAddOrUpdateEquipment" :readonly="!canAddOrUpdateEquipment" />
          </v-col>
          <v-col>
            <v-text-field v-model="label" label="Désignation*" outlined hide-details :readonly="!canAddOrUpdateEquipment" />
          </v-col>
        </v-row>
        <template v-if="isMaker">
          <v-row justify="center" align="center" v-if="isNew && canAddOrUpdateEquipment">
            <v-col>
              <v-autocomplete
                label="Client*"
                :items="clients"
                v-model="selectedClientId"
                item-value="id"
                item-text="name"
                hide-details
                clearable
                outlined
                prepend-inner-icon="business"
                :loading="!areClientsFetched"
              />
            </v-col>
          </v-row>
          <v-row justify="center" align="center" v-else>
            <v-col>
              <v-text-field
                label="Client"
                v-model="selectedClientName"
                hide-details
                outlined
                prepend-inner-icon="business"
                readonly
              />
            </v-col>
          </v-row>
        </template>
        <template v-else-if="isAggregator">
          <v-row justify="center" align="center" v-if="isNew && canAddOrUpdateEquipment">
            <v-col>
              <v-autocomplete
                label="Prestataire*"
                :items="makers"
                v-model="selectedMakerId"
                item-value="id"
                item-text="name"
                hide-details
                clearable
                outlined
                prepend-inner-icon="handyman"
                :loading="!areMakersFetched"
              />
            </v-col>
          </v-row>
          <v-row justify="center" align="center" v-else>
            <v-col>
              <v-text-field
                label="Prestataire"
                v-model="selectedMakerName"
                hide-details
                outlined
                prepend-inner-icon="handyman"
                readonly
              />
            </v-col>
          </v-row>
        </template>
        <v-row justify="center" align="center"  v-if="isNew || canAddOrUpdateEquipment">
          <v-col>
            <v-autocomplete
              label="Site*"
              :items="sites"
              v-model="selectedSiteId"
              :placeholder="hasSelectedClient ? `` : `Veuillez d'abord choisir un client`"
              item-value="id"
              item-text="name"
              hide-details
              outlined
              prepend-inner-icon="place"
              :disabled="!selectedClientId"
              :loading="!areSitesFetched"
            />
          </v-col>
          <v-btn
            xxxx-large
            icon
            class="mr-5"
            :disabled="!$canWrite || (!hasSelectedClient)"
            @click="$canWrite && (isNewSiteDialogOpened = true)"
            v-if="canAddOrUpdateEquipment"
          >
            <v-icon>add</v-icon>
          </v-btn>
        </v-row>
        <v-row justify="center" align="center" v-else>
          <v-col>
            <v-text-field
              label="Site"
              v-model="selectedSiteName"
              hide-details
              outlined
              prepend-inner-icon="place"
              readonly
            />
          </v-col>
        </v-row>
        <v-row justify="center" align="center">
          <v-col class="pr-2" cols="2">
            <v-text-field
              label="Étage"
              v-model="floor"
              hide-details
              :readonly="!canAddOrUpdateEquipment"
              outlined
            />
          </v-col>
          <v-col class="pl-2 pr-2" cols="3">
            <v-text-field
              label="Pièce"
              v-model="room"
              hide-details
              :readonly="!canAddOrUpdateEquipment"
              outlined
            />
          </v-col>
          <v-col class="pl-2" cols="7">
            <v-text-field
              label="Précision du lieu"
              v-model="placeDetails"
              hide-details
              :readonly="!canAddOrUpdateEquipment"
              outlined
            />
          </v-col>
        </v-row>
        <v-row justify="center" align="center">
          <v-col>
            <v-text-field v-model="brand" label="Marque" outlined hide-details :readonly="!canAddOrUpdateEquipment" />
          </v-col>
          <v-col>
            <v-text-field v-model="model" label="Modèle" outlined hide-details :readonly="!canAddOrUpdateEquipment" />
          </v-col>
        </v-row>
        <v-row justify="center" align="center">
          <v-col cols="4">
            <v-text-field v-model="serialNumber" label="Numéro de série" outlined hide-details :readonly="!canAddOrUpdateEquipment" />
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="installationDateFormatted"
              label="Date d'installation"
              outlined
              readonly
              hide-details
              v-if="!canAddOrUpdateEquipment"
            />
            <v-menu
              ref="menu"
              v-model="installationDateMenu"
              transition="scale-transition"
              offset-y
              min-width="auto"
              v-else
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="installationDateFormatted"
                  label="Date d'installation"
                  outlined
                  readonly
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  :clearable="canAddOrUpdateEquipment"
                />
              </template>
              <v-date-picker
                v-model="installationDate"
                :first-day-of-week="1"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="4">
            <v-select
              :items="equipmentConditions"
              v-model="condition"
              label="État"
              placeholder="Non défini"
              outlined
              item-text="label"
              item-value="id"
              hide-details
              :prepend-inner-icon="conditionObj ? conditionObj.icon : `question_mark`"
              :clearable="canAddOrUpdateEquipment"
              :readonly="!canAddOrUpdateEquipment"
            />
          </v-col>
        </v-row>
      </div>
      <v-divider />
      <v-row
        no-gutters
        justify="center"
        align="center"
        class="px-5 py-3"
        v-if="canAddOrUpdateEquipment"
      >
        <v-btn
          :disabled="!$canWrite"
          text
          color="error"
          class="pr-4"
          @click="$canWrite && (isConfirmRemoveDialogOpened = true)"
          :loading="isRemoving"
          v-if="!isSubmitting && hasEquipment"
        >
          <v-icon left>delete</v-icon>
          Supprimer
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          text
          class="mr-4"
          @click="$emit(`close`)"
          v-if="!isSubmitting && !isRemoving"
        >
          Annuler
        </v-btn>
        <v-btn
          :disabled="!$canWrite"
          color="primary darken-1"
          :loading="isSubmitting"
          type="submit"
          depressed
          v-if="!isRemoving"
        >
          {{ submitLabel }}
        </v-btn>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import { mapActions, mapMutations, mapState } from 'vuex';
import equipmentConditions from '@/lib/equipmentConditions'; // eslint-disable-line
import NewSiteDialog from '@/components/NewSiteModal.vue';

export default {
  props: {
    equipment: {
      type: Object,
      required: false,
      default: null,
    },
    updatable: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  data: () => ({
    equipmentConditions,
    isSubmitting: false,
    number: ``,
    label: ``,
    floor: ``,
    room: ``,
    placeDetails: ``,
    brand: ``,
    model: ``,
    serialNumber: ``,
    condition: null,
    selectedClientId: null,
    selectedSiteId: null,
    selectedMakerId: null,
    areClientsFetched: false,
    areSitesFetched: false,
    areMakersFetched: false,
    errors: [],
    isNewSiteDialogOpened: false,
    isConfirmRemoveDialogOpened: false,
    isRemoving: false,
    installationDateMenu: null,
    installationDate: null,
    installationDateFormatted: null,
  }),

  computed: {
    ...mapState(`model/user`, [`me`]),
    ...mapState(`model/client`, [
      `company`,
      `isAggregator`,
      `isMaker`,
      `isSelfMaker`,
    ]),
    ...mapState(`ui/equipmentModal`, [
      `clients`,
      `sites`,
      `makers`,
    ]),
    isNew() { return this.equipment === null; },
    submitLabel() { return this.isNew ? `Créer cet équipement` : `Modifier cet équipement`; },
    hasEquipment() { return Boolean(this.equipment); },
    hasSelectedClient() { return Boolean(this.selectedClientId); },
    hasSelectedSite() { return Boolean(this.selectedSiteId); },
    hasSelectedMaker() { return Boolean(this.selectedMakerId); },
    selectedClientName() {
      let name = ``;

      if (this.hasSelectedClient) {
        const client = _.find(this.clients, { id: this.selectedClientId });
        if (client) {
          name = client.name;
        }
      }

      return name;
    },
    selectedSiteName() {
      let name = ``;

      if (this.hasSelectedSite) {
        const site = _.find(this.sites, { id: this.selectedSiteId });
        if (site) {
          name = site.name;
        }
      }

      return name;
    },
    selectedMakerName() {
      let name = ``;

      if (this.hasSelectedMaker) {
        const maker = _.find(this.makers, { id: this.selectedMakerId });
        if (maker) {
          name = maker.name;
        }
      }

      return name;
    },
    canAddOrUpdateEquipment() { return Boolean(this.updatable && (this.me.client.isRoot || this.isSelfMaker || this.isAggregator) && this.me.role === `administrator`); },
    clientId() { return this.hasSelectedClient ? this.selectedClientId : null; },
    makerId() { return !this.isSelfMaker ? this.company.id : null; },
    conditionObj() {
      const conditionObj = _.find(equipmentConditions, { id: this.condition });

      return conditionObj || null;
    },
  },

  methods: {
    ...mapMutations(`ui/equipmentModal`, [
      `SET_CLIENTS`,
      `SET_MAKERS`,
    ]),
    ...mapActions(`ui/equipmentModal`, [
      `addEquipment`,
      `updateEquipment`,
    ]),
    ...mapActions(`ui/equipmentModal`, {
      storeFetchClients: `fetchClients`,
      storeFetchSites: `fetchSites`,
      storeFetchMakers: `fetchMakers`,
    }),
    ...mapActions(`model/equipment`, [
      `removeEquipment`,
    ]),
    async fetchClients() {
      this.areClientsFetched = false;
      await this.storeFetchClients();
      this.areClientsFetched = true;
    },
    async fetchSites() {
      this.areSitesFetched = false;
      if (this.hasSelectedClient) {
        await this.storeFetchSites(this.selectedClientId);
        if (this.sites.length === 1) {
          this.selectedSiteId = this.sites[0].id;
        }
      }
      this.areSitesFetched = true;
    },
    async fetchMakers() {
      this.areMakersFetched = false;
      await this.storeFetchMakers();
      this.areMakersFetched = true;
    },
    async newSiteCreated({ id }) {
      this.isNewSiteDialogOpened = false;
      await this.fetchSites();
      this.selectedSiteId = id;
    },
    async submit() {
      this.errors = [];
      if (!this.label || !_.trim(this.label).length) { this.errors.push(`La désignation est obligatoire`); }
      if (!this.selectedClientId) { this.errors.push(`Le client est obligatoire`); }
      if (!this.selectedSiteId) { this.errors.push(`Le site est obligatoire`); }

      if (this.errors.length) { return; }

      let id;
      let installedAt = null;

      if (this.hasEquipment) { id = this.equipment.id; }
      if (this.installationDate) { installedAt = moment(this.installationDate, `YYYY-MM-DD`).format(`YYYY-MM-DD HH:mm:ss`); }

      const action = this.isNew ? `addEquipment` : `updateEquipment`;

      this.isSubmitting = true;
      const wasNew = this.isNew;

      const dbEquipment = await this[action]({
        id,
        number: this.number,
        label: this.label,
        floor: this.floor,
        room: this.room,
        placeDetails: this.placeDetails,
        brand: this.brand,
        model: this.model,
        serialNumber: this.serialNumber,
        condition: this.condition,
        Client_id: this.selectedClientId,
        Site_id: this.selectedSiteId,
        Maker_id: this.selectedMakerId,
        installedAt,
      });

      this.isSubmitting = false;

      if (dbEquipment) {
        const event = wasNew ? `created` : `updated`;
        const snackbarMessage = wasNew ? `Équipement créé` : `Équipement modifié`;

        this.$emit(event, dbEquipment);

        this.$store.dispatch(`showSnackbar`, {
          color: `success`,
          message: snackbarMessage,
        });
      }
    },
    async remove() {
      this.isRemoving = true;
      await this.removeEquipment(this.equipment.id);
      this.isRemoving = false;
      this.$emit(`removed`, this.equipment);
    },
  },

  async beforeMount() {
    if (this.hasEquipment) {
      this.number = this.equipment.number;
      this.label = this.equipment.label;
      this.floor = this.equipment.floor || ``;
      this.room = this.equipment.room || ``;
      this.placeDetails = this.equipment.placeDetails || ``;
      this.brand = this.equipment.brand;
      this.model = this.equipment.model;
      this.serialNumber = this.equipment.serialNumber;
      this.condition = this.equipment.condition || null;
      this.selectedClientId = this.equipment.Client_id;
      this.selectedSiteId = this.equipment.Site_id;
      this.selectedMakerId = this.equipment.Maker_id;
      if (this.equipment.installedAt) {
        this.installationDate = moment(this.equipment.installedAt, `YYYY-MM-DD HH:mm:ss`).format(`YYYY-MM-DD`);
      }
    }
    const promises = [];
    if (this.isMaker || this.isSelfMaker) {
      promises.push(this.fetchClients());
      this.SET_MAKERS([this.company]);
    } else {
      promises.push(this.fetchMakers());
      this.SET_CLIENTS([this.company]);
    }

    promises.push(this.fetchSites());

    await Promise.all(promises);

    if (this.clients.length === 1) {
      this.selectedClientId = this.clients[0].id;
    }

    if (this.makers.length === 1) {
      this.selectedMakerId = this.makers[0].id;
    }
  },

  watch: {
    selectedClientId(selectedClientId, oldValue) {
      if (oldValue) {
        this.selectedSiteId = null;
      }
      if (this.hasSelectedClient) {
        this.fetchSites();
      }
    },
    installationDateFormatted(date) {
      if (date === null) {
        this.installationDate = null;
      }
    },
    installationDate(date) {
      if (!date && this.installationDateFormatted !== null) {
        this.installationDateFormatted = null;
      } else if (date) {
        this.installationDateFormatted = moment(date, `YYYY-MM-DD`).format(`DD/MM/YYYY`);
      }
    },
  },

  components: {
    NewSiteDialog,
  },
};
</script>

<style lang="scss">
</style>
